import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { MetaPagination } from '@lib/types';
import classNames from 'classnames';
import { usePagination } from 'hook/usePagination';
import { useWindowSize } from 'hook/useWindowSize';
import Link from 'next/link';

type PaginationProps = MetaPagination;

export default function Pagination({
  page,
  pageCount,
  pageSize,
}: PaginationProps) {
  const size = useWindowSize();

  const paginationRange = usePagination({
    page,
    pageCount,
    siblingCount: size.width && size.width < 640 ? 0 : 1,
  });

  return (
    <div className="flex items-center justify-center mx-auto">
      <div>
        <nav className="isolate inline-flex sm:gap-2" aria-label="Pagination">
          {page > 1 ? (
            <Link
              href={`/news?page=${page - 1}&pageSize=${pageSize}`}
              className="relative inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-blue-800 hover:bg-blue-200 hover:text-blue-800 focus:z-20 focus:outline-offset-0 rounded-full">
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-6 w-6" />
            </Link>
          ) : (
            <span className="relative inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-gray-300 focus:z-20 focus:outline-offset-0">
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
            </span>
          )}
          {(paginationRange || []).map((item, index) => {
            if (item === '...') {
              return (
                <span
                  key={index}
                  className="relative inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-16 sm:text-19 -mt-[9px] leading-100 text-blue-800 focus:outline-offset-0">
                  ...
                </span>
              );
            }

            return (
              <Link
                key={index}
                href={`/news?page=${item}&pageSize=${pageSize}`}
                aria-current={item === page ? 'page' : undefined}
                className={classNames(
                  `relative z-10 inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-16 sm:text-19 -mt-[2px] leading-100 text-blue-800 focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 rounded-full`,
                  {
                    'bg-blue-500 text-white hover:bg-blue-800': item === page,
                  },
                  {
                    'hover:bg-blue-200 hover:text-blue-800': item !== page,
                  },
                )}>
                <span className="-mt-[2px] sm:-mt-[3px]">{item}</span>
              </Link>
            );
          })}
          {page < pageCount ? (
            <Link
              href={`/news?page=${page + 1}&pageSize=${pageSize}`}
              className="relative inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-blue-800 hover:bg-blue-200 hover:text-blue-800 focus:z-20 focus:outline-offset-0 rounded-full">
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-6 w-6" />
            </Link>
          ) : (
            <span className="relative inline-flex justify-center items-center w-12 h-12 sm:w-10 sm:h-10 text-gray-300 focus:z-20 focus:outline-offset-0">
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
            </span>
          )}
        </nav>
      </div>
    </div>
  );
}
